import { useEffect, useState } from "react";

import {
    ArrowButton, TextField, Checkbox, Select, Button,
    TableMetadata, TableStateWithFilters, useTable, Label, ColumnSortEnum
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import { codeBookToSelectData, hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

//import { searchInstitutionFilterList } from "../../api/Institution/InstitutionApi";
import {
    searchRequestData, getRequestDataExport,
    confirmRequest, generateRequest
} from "../../api/Request/RequestApi";
import { ContentArea, FormCard, InstitutionSelect, ListTable, SearchCard, ToolbarTop } from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import { useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
    ValidationFailure, GreatGrandParentCodeBookPagedFilter
} from "../../models";
import {
    RegistrationFormStatus, RequestData, RequestDataSearch, RequestSort,
    RequestStatus, RequestType, RequestList
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import { useGetSchoolYearCurrentQuery, useGetSchoolYearListQuery } from "../../queries/codeBookQuery";
import { registrationFormEditUrl } from "../../routes/routes";
import { FormatUtils, MsgUtils, TableUtils } from "../../utils";


export function RegistrationFormList() {
    const { t } = useTranslation();
    const history = useHistory();
    const { handleChange } = useInput();
    const auth = useAuth();
    //STORAGE KEY
    const storageKey = 'EnrollmentRegistrationFormListSearch';
    const { data: schoolYear } = useGetSchoolYearCurrentQuery();
    // onlyEnrollmentApproved: true,
    //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
    const createInitialTableState = () => {
        return TableUtils.CreateInitialTableState<RequestDataSearch>(
            storageKey,
            TableUtils.tableDefaultState(RequestSort.Code, ColumnSortEnum.DESC, 10)
        )
    }
    const [initialTableState] = useState<TableStateWithFilters<RequestDataSearch>>(() => createInitialTableState());
    //INIT SEARCH FILTER
    const createInitialRequestDataSearch = (): RequestDataSearch => {
        return {
            schoolYearId: schoolYear?.id,
            institutionIds:
                initialTableState.institutionIds
                    ? initialTableState.institutionIds
                    : defaultInstitutionIds,
            code: initialTableState.code,
            firstName: initialTableState.firstName,
            lastName: initialTableState.lastName,
            oib: initialTableState.oib,
            requestType: initialTableState.requestType,
            requestStatus: initialTableState.requestStatus,

            onlyRequests: initialTableState.onlyRequests,
            onlyWithAddReasonDelayPreviousYear: initialTableState.onlyWithAddReasonDelayPreviousYear,
            onlyNotSchoolCandidate: initialTableState.onlyNotSchoolCandidate,
            onlyOutsideEnrollmentArea: initialTableState.onlyOutsideEnrollmentArea,
            onlyLanguageCourseRequired: initialTableState.onlyLanguageCourseRequired,
            onlyMinistryLanguageCourseRequired: false,
            onlyEnrollmentApproved: true, // FIXED - IMPORTANT
            onlyEnrolled: initialTableState.onlyEnrolled,
            outsideEnrollmentAreaStatus: initialTableState.outsideEnrollmentAreaStatus,
        }
    }
    const defaultInstitutionIds: number[] =
        auth.currentRole?.claimType === ClaimType.Institution
            && (auth.currentRole?.locations?.length ?? 0) > 0
            && auth.currentRole?.locations
            && auth.currentRole?.locations[0]
            ? [auth!.currentRole!.locations![0]]
            : [] as number[];
    //TABLE DATA
    const defaultTableData = TableUtils.tableDefaultData<RequestData>()
    const [tableData, setTableData] = useState<PagedResult<RequestData>>(defaultTableData);
    //SET SEARCH FILTERS
    const [requestDataSearch, setRequestDataSearch] = useState<RequestDataSearch>(createInitialRequestDataSearch());
    const [tableSearch, setTableSearch] = useState<RequestDataSearch>(createInitialRequestDataSearch());
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [clearFilter, setClearFilter] = useState<boolean>(true);
    //TABLE METADATA
    const tableMetadata: TableMetadata<RequestData> = {
        rowKey: "id",
        columns: [
            {
                columnKey: "select",
                header: "",
                accessor: (item: RequestData) => {
                    return (
                        <div>
                            <Checkbox
                                label={""}
                                name={nameof<typeof item>("id")}
                                value={item.id}
                                checked={item.isSelected}
                                onChange={handleItemCheckedChange}
                            />
                        </div>)
                },
            },
            {
                columnKey: "code",
                header: t("common.schoolYearModel.code"),
                accessor: (item: RequestData) => {
                    return (<div className={"text-nowrap text-right"}>
                        {item.code}
                    </div>)
                },
                sortBy: RequestSort.Code,
            },
            {
                columnKey: "firstName",
                header: t("common.schoolCandidateModel.lastName") + "/" + t("common.schoolCandidateModel.firstName"),
                accessor: (item: RequestData) => {
                    return (
                        <div>
                            <div className={"text-nowrap"}>
                                {item.lastName},&nbsp;{item.firstName}
                            </div>
                            <div className={"text-nowrap"}>
                                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
                            </div>
                        </div>)
                },
                sortBy: RequestSort.LastName,
            },
            {
                columnKey: "dateOfBirth",
                header: t("common.schoolCandidateModel.dateOfBirth"),
                accessor: (item: RequestData) => {
                    return (<div className={"text-nowrap text-right"}>
                        {item.dateOfBirth ? hrDate(item.dateOfBirth) : ""}
                    </div>)
                },
                sortBy: RequestSort.Birthday,
            },
            {
                columnKey: "permanentAddress",
                header: t("common.schoolCandidateModel.permanentAddress") + "/" + t("common.schoolCandidateModel.temporaryAddress"),
                accessor: (item: RequestData) => {
                    return (
                        <>
                            <div className={"text-nowrap"}>
                                <b>{item.permanentAddress?.street}&nbsp;{item.permanentAddress?.houseNumber},&nbsp;{item.permanentAddress?.town} {/*,&nbsp;{item.permanentAddress.municipality},&nbsp;{item.permanentAddress.county*/}</b>
                            </div>
                            {item.temporaryAddress &&
                                (<div className={"text-nowrap"}>
                                    {item.temporaryAddress.street}&nbsp;{item.temporaryAddress.houseNumber},&nbsp;{item.temporaryAddress.town} {/*,&nbsp;{item.temporaryAddress.municipality},&nbsp;{item.temporaryAddress.county*/}
                                </div>)
                            }
                        </>
                    )
                },
                sortBy: RequestSort.Address,
            },
            {
                columnKey: "type",
                header: t("common.requestModel.requestType"),
                accessor: (item: RequestData) => {
                    return (
                        <>
                            {/*                     <Badge pill bg={FormatUtils.getRequestTypeColor(item.requestType)} >
                            {types.find(v => v.id === item.requestType)?.name}
                        </Badge>
     */}
                            <Label color={FormatUtils.getRequestTypeColor(item.requestType)} className="text-center" outline={false} fullWidth={false}>
                                {types.find(v => v.id === item.requestType)?.name}
                            </Label>
                        </>
                    )
                },
                sortBy: RequestSort.Type,
            },
            {
                columnKey: "status",
                header: t("common.requestModel.requestStatus"),
                accessor: (item: RequestData) => {
                    return (
                        <>
                            {/*                     <Badge pill bg={FormatUtils.getRequestStatusColor(item.status)} >
                            {statuses.find(v => v.id === item.status)?.name}
                        </Badge>
     */}
                            <Label color={FormatUtils.getRequestStatusColor(item.status)} className="text-center" outline={false} fullWidth={false}>
                                {statuses.find(v => v.id === item.status)?.name}
                            </Label>
                        </>
                    )
                },
                sortBy: RequestSort.Status,
            },
            {
                columnKey: "confirmed",
                header: t("common.requestModel.confirmedEnrollment"),
                accessor: (item: RequestData) => {
                    return (
                        <>
                            {item.status === RequestStatus.EnrollmentConfirmed
                                || item.status === RequestStatus.RegistrationFormCreated
                                //|| item.status === RequestStatus.RegistrationSigned
                                || item.status === RequestStatus.Registered ? t("common.yes") : t("common.no")}
                        </>
                    )
                },
            },
            {
                columnKey: "registrationFormStatus",
                header: t("common.requestModel.registrationFormStatus"),
                accessor: (item: RequestData) => {
                    return (
                        <>
                            {registrationFormStatuses.find(v => v.id === item.registrationFormStatus)?.name ?? t("registrationForm.notCreated")}
                        </>
                    )
                },
            },
            {
                columnKey: "details",
                header: "",
                accessor: (item: RequestData) => {
                    if (item.status === RequestStatus.RegistrationFormCreated
                        //|| item.status === RequestStatus.RegistrationSigned
                        || item.status === RequestStatus.Registered)
                        return (<div className={"text-right"}>
                            <ArrowButton
                                ariaLabel={t("common.details")}
                                onClick={() =>
                                    handleDetailsClicked(item.id)
                                } />
                        </div>)
                    else return <></>
                }
            }
        ],
        rowToolTip: () => undefined
    };
    //SET TABLE
    const [tableProps, tableState, setFilters] = useTable<RequestDataSearch>(
        {
            metadata: tableMetadata,
            data: tableData!.results,
            pagination: true,
            paginationPage: initialTableState.page,
            paginationPageSize: initialTableState.pageSize as any,
            totalItems: tableData!.rowCount,
            defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
        }
    );
    //FILTER DATA
    //const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>([] as CodeBook[]);
    const [statuses] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(RequestStatus, "requestStatus"));
    const [types] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(RequestType, "requestType"));
    const { data: schoolYears } = useGetSchoolYearListQuery();
    const [registrationFormStatuses] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(RegistrationFormStatus, "registrationFormStatus"));
    //EDIT DATA
    const createDefaultRequestList = (): RequestList => {
        return {
            idList: [],
        };
    };
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [requestList, setRequestList] = useState<RequestList>(createDefaultRequestList);
    const [errors, setErrors] = useState<ValidationFailure[]>()
    const [confirmState, setConfirmState] = useState<any>()
    const [confirmStateGenerate, setConfirmStateGenerate] = useState<any>()

    // useEffect(() => {
    //     (async () => {
    //         if (auth.currentRole?.claimType === ClaimType.Institution && auth.currentRole?.locations) {
    //             const filter: GreatGrandParentCodeBookPagedFilter = {
    //                 page: 1,
    //                 pageSize: 30,
    //                 search: null,
    //                 ids: auth.currentRole.locations,
    //             };
    //             const result = await searchInstitutionFilterList(filter);
    //             if (result && result.results && result.results.length > 0) {
    //                 setFilteredInstitutions(result.results);
    //             }
    //         }
    //     })();
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    useEffect(() => {
        if (clearFilter) {
            setClearFilter(false);
            setFilterChanged(false);
        }
        else
            setFilterChanged(true);
        TableUtils.RemoveSessionStorageItem(storageKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestDataSearch]);

    useEffect(() => {
        (async () => {
            var request = TableUtils.GetSearchRequest<RequestDataSearch>(tableSearch, tableState);
            var itemList = await searchRequestData(request);
            if (itemList !== null && itemList.results.length > 0) {
                setTableData(itemList);
            }
            else {
                setTableData(defaultTableData);
            }
            clearAssigmentButtons();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    const handleInstitutionChange = (id: number) => {
        //const institutionList = e.target.value as CodeBook[];
        const newSearch: RequestDataSearch = {
            ...requestDataSearch,
            //institutionIds: institutionList && institutionList.length > 0 ? institutionList.map(val => val.id) : [],
            institutionIds: !isNaN(id) ? [id] : [],
        };
        setRequestDataSearch(newSearch);
    };
    const handleDetailsClicked = (id?: number) => {
        if (id) {
            TableUtils.SetSessionStorageItem<RequestDataSearch>(storageKey, tableSearch, tableState);
            history.push(
                registrationFormEditUrl(
                    id!
                )
            );
        }
    };
    const handleSearchClicked = (e: any) => {
        setTableSearch(requestDataSearch);
        setFilterChanged(false);
        doSearch();
    };
    const handleClearSearchClicked = () => {
        setClearFilter(true);
        const newRequestDataSearch: RequestDataSearch = {
            schoolYearId: schoolYear?.id,
            institutionIds: defaultInstitutionIds,

            onlyRequests: false,
            onlyWithAddReasonDelayPreviousYear: false,
            onlyNotSchoolCandidate: false,
            onlyOutsideEnrollmentArea: false,
            onlyLanguageCourseRequired: false,
            onlyMinistryLanguageCourseRequired: false,
            onlyEnrollmentApproved: true,
            onlyEnrolled: false,
            outsideEnrollmentAreaStatus: initialTableState.outsideEnrollmentAreaStatus,
        };
        setRequestDataSearch(newRequestDataSearch);
        setTableSearch(newRequestDataSearch)
        doSearch();
    };
    const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
        await getRequestDataExport(requestDataSearch, exportType);
    };
    const doSearch = () => {
        setFilters({ ...tableSearch });
    };
    //EDIT HANDLERS
    const clearAssigmentButtons = () => {
        setSelectAll(false);
        setRequestList(createDefaultRequestList());
    }
    const handleItemCheckedChange = (e: any) => {
        const itemId = Number.parseInt(e.target.value);
        const isChecked = e.target.checked as boolean;
        if (itemId) {
            var request = tableData.results.find(val => val.id === itemId);
            var list = requestList.idList;
            if (isChecked && request) {
                list.push(itemId);
                request!.isSelected = true;
            } else {
                list = list.filter(val => val !== itemId);
                setSelectAll(false);
                request!.isSelected = false;
            }
            const assignmentList: RequestList = {
                ...requestList,
                idList: list,
            };
            setRequestList(assignmentList);
            //setAssignmentButtons(newUpdateInstitutionAssignment);
        }
    };
    const handleSelectAllCheckedChange = (e: any) => {
        const isChecked = !selectAll;
        setSelectAll(isChecked);
        const newResults = tableData.results.map(val => { val.isSelected = isChecked; return val; });
        const newTableData: PagedResult<RequestData> = {
            ...tableData,
            results: newResults,
        };
        const newUpdate: RequestList = {
            ...requestList,
            idList: newResults.filter(val => val.isSelected).map(val => val.id),
        }
        setTableData(newTableData);
        setRequestList(newUpdate);
        //setAssignmentButtons(newUpdate);
    };
    const handleConfirmClicked = async () => {
        var result = await confirmRequest(requestList);
        if (result) {
            if (result.isValid) {
                setConfirmState("message")
            }
            else {
                setErrors(result.validationFailures);
            }
            doSearch();
        } else {
            setConfirmState("error")
        }
    };
    const handleGenerateClicked = async () => {
        var result = await generateRequest(requestList);
        if (result) {
            if (result.isValid) {
                setConfirmStateGenerate("message")
            }
            else {
                setErrors(result.validationFailures);
            }
            doSearch();
        } else {
            setConfirmStateGenerate("error")
        }
    };
    const getConfirmEnabled = (): boolean => {
        const requestDataList = tableData.results.filter(val => val.isSelected);
        const result = requestDataList.length > 0 && requestDataList.find(val =>
            val.status !== RequestStatus.EnrollmentApproved
            && val.status !== RequestStatus.ProcedureApproved
            && val.status !== RequestStatus.DecisionOnAppropriateProgram
        ) === undefined;
        return result;
    };
    const getGenerationEnabled = (): boolean => {
        const requestDataList = tableData.results.filter(val => val.isSelected);
        const result = requestDataList.length > 0 && requestDataList.find(val => val.status !== RequestStatus.EnrollmentConfirmed) === undefined;
        return result;
    };
    return (
        <ContentArea>
            <ToolbarTop
                pageTitle={t("common.requestModel.pageName")}
                showAdd={"hide"}
                errors={errors}
            />
            <SearchCard
                onClickSearch={handleSearchClicked}
                onClickClearSearch={handleClearSearchClicked}
                isFilterChanged={filterChanged}
                cardTitle={t("common.requestModel.searchCardName")}
                hideDivider={true}
            >
                <Row>
                    <Col md={12}>
                        <Select
                            label={t("common.schoolCandidateModel.schoolYearId")}
                            name={nameof<typeof requestDataSearch>("schoolYearId")}
                            value={requestDataSearch.schoolYearId}
                            required={true}
                            onChange={handleChange(setRequestDataSearch)}
                            selectData={codeBookToSelectData(schoolYears)}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={8}>
                            <InstitutionSelect
                                label={t("common.schoolCandidateModel.institutionList")}
                                name={nameof<typeof requestDataSearch>("institutionIds")}
                                value={requestDataSearch.institutionIds && requestDataSearch.institutionIds.length > 0 ? requestDataSearch.institutionIds[0] : undefined}
                                countyIds={auth.currentRole?.claimType === ClaimType.County ? auth.currentRole.locations : undefined}
                                ids={auth.currentRole?.claimType === ClaimType.Institution ? auth.currentRole.locations : undefined}
                                onlyByEnrollmentArea={false}
                                excludeHandicapped={false}
                                excludePrivate={false}
                                excludeSatellite={false}
                                onChange={(e) => handleInstitutionChange(Number.parseInt(e.target.value))}
                                showUser={true}
                            />
                    </Col>
                    <Col md={3}>
                        <TextField
                            label={t("common.schoolYearModel.code")}
                            name={nameof<typeof requestDataSearch>("code")}
                            value={requestDataSearch.code}
                            onChange={handleChange(setRequestDataSearch)}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={4}>
                        <TextField
                            label={t("common.schoolCandidateModel.lastName")}
                            name={nameof<typeof requestDataSearch>("lastName")}
                            value={requestDataSearch.lastName}
                            onChange={handleChange(setRequestDataSearch)}
                        />
                    </Col>
                    <Col md={4}>
                        <TextField
                            label={t("common.schoolCandidateModel.firstName")}
                            name={nameof<typeof requestDataSearch>("firstName")}
                            value={requestDataSearch.firstName}
                            onChange={handleChange(setRequestDataSearch)}
                        />
                    </Col>
                    <Col md={3}>
                        <TextField
                            label={t("common.schoolCandidateModel.oib")}
                            name={nameof<typeof requestDataSearch>("oib")}
                            value={requestDataSearch.oib}
                            onChange={handleChange(setRequestDataSearch)}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={3}>
                        <Select
                            label={t("common.requestModel.requestType")}
                            name={nameof<typeof requestDataSearch>("requestType")}
                            value={requestDataSearch.requestType}
                            onChange={handleChange(setRequestDataSearch)}
                            selectData={codeBookToSelectData(types)}
                            emptyOption={{ text: t("common.all"), value: undefined }}
                        />
                    </Col>
                    <Col md={3}>
                        <Select
                            label={t("common.requestModel.requestStatus")}
                            name={nameof<typeof requestDataSearch>("requestStatus")}
                            value={requestDataSearch.requestStatus}
                            onChange={handleChange(setRequestDataSearch)}
                            selectData={codeBookToSelectData(statuses)}
                            emptyOption={{ text: t("common.all"), value: undefined }}
                        />
                    </Col>
                </Row>
            </SearchCard>
            <FormCard
            >
                <Row className="m-1 pb-2 pt-2 bg-light border">
                    <Col md={2} className="pt-3">
                        <Checkbox
                            label={t("common.selectAll")}
                            name={"selectAll"}
                            checked={selectAll}
                            onChange={handleSelectAllCheckedChange}
                        />
                    </Col>
                    <Col md={3} className="pt-3">
                        <Button onClick={() => setConfirmState("confirm")}
                            outline={true}
                            size="sm"
                            disabled={!getConfirmEnabled()}>
                            {t("common.requestModel.enrollmentConfirm")}
                        </Button>
                    </Col>
                    <Col md={3} className="pt-3">
                        <Button onClick={() => setConfirmStateGenerate("confirm")}
                            size="sm"
                            disabled={!getGenerationEnabled()}>
                            {t("common.requestModel.registrationGenerate")}
                        </Button>
                    </Col>
                </Row>
            </FormCard>
            <ListTable
                tableProps={tableProps}
                onClickDownload={(exportType: ExportTypeEnum) => handleDownloadClicked(exportType)}
                showDownload
            />
            <ConfirmModal
                title={t("common.requestModel.enrollmentConfirm")}
                confirmState={confirmState}
                confirmMessage={t("common.requestModel.confirmMessage")}
                successMesage={t("common.requestModel.successMesage")}
                errorMessage={t("common.requestModel.errorMessage")}
                onClickClose={() => setConfirmState(undefined)}
                onClickConfirm={handleConfirmClicked}
            />
            <ConfirmModal
                title={t("common.requestModel.registrationGenerate")}
                confirmState={confirmStateGenerate}
                confirmMessage={t("common.requestModel.confirmMessageGenerate")}
                confirmDescription={t("common.requestModel.confirmDescriptionGenerate")}
                successMesage={t("common.requestModel.successMesageGenerate")}
                errorMessage={t("common.requestModel.errorMessageGenerate")}
                onClickClose={() => setConfirmStateGenerate(undefined)}
                onClickConfirm={handleGenerateClicked}
            />

        </ContentArea >
    );
}